/**
 * Luke Cage — „Marvel: Redemption Row".
 *
 * Luke dostáva zo súboja o 2 combat damage menej. Keď sa bráni a nedostane nič,
 * súboj vyhral — aj keď kartu nezahral. Misty Knight je ranged sidekick.
 */

import { neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, drawCard, endTurnNow, gainAction, getMap, shuffleDeck } from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex, SpaceId } from '../types.js';
import { draw, regroup } from './common.js';
import { askBoost, askMove, fighterOrNull } from './helpers.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const LUKE = 'luke-cage';
const MISTY = 'misty-knight';
const STILL = 'luke-cage.still-standing';
const POWER = 'luke-cage.power-man';

const figure = (ctx: Ctx, player: PlayerIndex, defId: string) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === defId) ?? null;

/** „If you won the combat, draw 2 cards." */
const getPaid: CardEffect = {
  after: (e) => {
    if (!e.won) return;
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
  },
};

/** „DURING COMBAT: You may BOOST this attack." */
const heroForHire: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Hero for Hire: smieš boostovať útok'),
};

/**
 * „If you won the combat, choose 2 cards in your discard pile and shuffle
 * them into your deck." Karty z tohto súboja v odhode ešte nie sú.
 */
function askStill(ctx: Ctx, player: PlayerIndex, source: string, left: number): void {
  const discard = ctx.state.players[player]?.discard ?? [];
  if (left <= 0 || discard.length === 0) {
    askChoice(ctx, {
      kind: 'auto',
      player,
      source,
      label: 'Still Standing: zamiešať balíček',
      optional: false,
      follow: { kind: 'effect', effect: STILL, data: 'shuffle' },
    });
    return;
  }
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label: `Still Standing: vyber kartu z odhodu, ktorá sa zamieša do balíčka (${3 - left} z 2)`,
    optional: false,
    from: 'discard',
    cards: discard.slice(),
    purpose: 'effect',
    left,
  });
}

const stillStanding: CardEffect = {
  after: (e) => {
    if (e.won) askStill(e.ctx, e.player, e.cardId, 2);
  },
  onCardChosen: (ctx, choice, cardId) => {
    const seat = ctx.state.players[choice.player];
    if (!seat || !choice.source) return;
    const index = seat.discard.indexOf(cardId);
    if (index === -1) return;
    seat.discard.splice(index, 1);
    seat.deck.push(cardId);
    askStill(ctx, choice.player, choice.source, (choice.left ?? 1) - 1);
  },
  onFollow: (ctx, choice) => shuffleDeck(ctx, choice.player),
};

/** „If you lost the combat, deal damage to the opposing fighter equal to the amount of combat damage you took." */
const skinLikeTitanium: CardEffect = {
  after: (e) => {
    if (e.won || !e.opponentFighterId || !e.damage) return;
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: `Skin Like Titanium: ${e.damage} poškodenia útočníkovi`,
      optional: false,
      amount: e.damage,
      targets: [e.opponentFighterId],
    });
  },
};

/**
 * „Move the opposing fighter up to 3 spaces. You may move them through other
 * opposing fighters. Deal 1 damage to them and each opposing fighter they moved through."
 */
const powerMan: CardEffect = {
  after: (e) => {
    if (!e.opponentFighterId) return;
    askMove(e, {
      fighters: [e.opponentFighterId],
      max: 3,
      through: true,
      label: 'Power Man: presuň súperovu figúrku až o 3 (smie cez ďalších súperov)',
      follow: { kind: 'damageThrough', amount: 1, label: 'Power Man', each: true },
    });
  },
};

/** „IMMEDIATELY: Misty Knight may swap spaces with Luke Cage. If she does, Luke Cage is now the defender." */
const gotMyBack: CardEffect = {
  immediate: (e) => {
    const misty = fighterOrNull(e.ctx, e.fighterId);
    const luke = figure(e.ctx, e.player, LUKE);
    if (misty?.defId !== MISTY || !luke) return;
    askChoice(e.ctx, {
      kind: 'swap',
      player: e.player,
      source: e.cardId,
      label: "Got My Back: Misty Knight si smie vymeniť miesto s Lukom — brániť sa bude Luke",
      optional: true,
      fighterId: misty.id,
      candidates: [luke.id],
    });
  },
};

/** „IMMEDIATELY: Cancel all effects on your opponent's card. AFTER: If you won the combat, end the turn." */
const trashTalk: CardEffect = {
  cancelsOpponent: true,
  after: (e) => {
    if (e.won) endTurnNow(e.ctx);
  },
};

/** „Move the opposing fighter up to 3 spaces." */
const pushback: CardEffect = {
  after: (e) => {
    if (e.opponentFighterId) askMove(e, { fighters: [e.opponentFighterId], max: 3, label: 'Pushback: presuň súperovu figúrku až o 3' });
  },
};

/** „If Misty Knight is adjacent to the opposing fighter, the value of this card is 6 instead." */
const daughterOfTheDragon: CardEffect = {
  during: (e, value) => {
    const misty = figure(e.ctx, e.player, MISTY);
    const target = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!misty || !target) return value;
    return neighbors(getMap(e.ctx), misty.spaceId).includes(target.spaceId) ? 6 : value;
  },
};

/** Vzdialenosť v krokoch po čiarach mapy (bez ohľadu na figúrky). */
function distances(ctx: Ctx, from: SpaceId): Map<SpaceId, number> {
  const map = getMap(ctx);
  const dist = new Map<SpaceId, number>([[from, 0]]);
  let frontier = [from];
  while (frontier.length) {
    const next: SpaceId[] = [];
    for (const id of frontier) {
      for (const n of neighbors(map, id)) {
        if (dist.has(n)) continue;
        dist.set(n, dist.get(id)! + 1);
        next.push(n);
      }
    }
    frontier = next;
  }
  return dist;
}

/** „Place Luke Cage adjacent to the nearest opposing fighter. Gain 1 action." Pri zhode vyberá hráč. */
const wheresMyMoney: CardEffect = {
  scheme: (e) => {
    const luke = figure(e.ctx, e.player, LUKE);
    if (luke) {
      const dist = distances(e.ctx, luke.spaceId);
      const enemies = e.ctx.state.fighters.filter((f) => areEnemies(e.ctx.state, f.owner, e.player));
      const nearest = Math.min(...enemies.map((f) => dist.get(f.spaceId) ?? Infinity));
      const spaces = new Set<SpaceId>();
      for (const enemy of enemies.filter((f) => (dist.get(f.spaceId) ?? Infinity) === nearest)) {
        for (const id of neighbors(getMap(e.ctx), enemy.spaceId)) {
          if (!e.ctx.state.fighters.some((f) => f.spaceId === id && f.id !== luke.id)) spaces.add(id);
        }
      }
      spaces.delete(luke.spaceId);
      askChoice(e.ctx, {
        kind: 'place',
        player: e.player,
        source: e.cardId,
        label: "Where's My Money: polož Luka vedľa najbližšej súperovej figúrky",
        optional: false,
        fighterId: luke.id,
        spaces: [...spaces],
      });
    }
    gainAction(e.ctx, e.player);
  },
};

export const lukeCageCards: EffectRegistry = {
  'luke-cage.commanding-impact': draw(1),
  'luke-cage.get-paid': getPaid,
  'luke-cage.hero-for-hire': heroForHire,
  [STILL]: stillStanding,
  'luke-cage.skin-like-titanium': skinLikeTitanium,
  [POWER]: powerMan,
  'luke-cage.got-my-back': gotMyBack,
  'luke-cage.trash-talk': trashTalk,
  'luke-cage.pushback': pushback,
  'luke-cage.daughter-of-the-dragon': daughterOfTheDragon,
  'luke-cage.regroup': regroup,
  'luke-cage.wheres-my-money': wheresMyMoney,
  // Sweet Christmas: bez efektu, len hodnota 6.
};

export const lukeCageSpecial: HeroSpecial = {
  name: 'Nepriestrelný',
  /** „Luke Cage takes 2 less combat damage from attacks." */
  combatDamageTaken: (ctx, _player, fighterId, damage) =>
    fighterOrNull(ctx, fighterId)?.defId === LUKE ? Math.max(0, damage - 2) : damage,
};
